import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './AdminPanel.css';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  status?: string;
  is_admin?: number | boolean;
  created_at: string;
}

interface AdminComment {
  id: number;
  content: string;
  created_at: string;
  user_id: number;
  username?: string;
  duck_log_id: number;
}

interface AdminCommentReport {
  id: number;
  comment_id: number;
  reporter_id: number;
  reason?: string | null;
  created_at: string;
  comment_content?: string;
  comment_user?: string;
  reporter_username?: string;
}

interface AdminDuckLog {
  id: number;
  ship_name: string;
  location?: string;
  found_date: string;
  created_at: string;
  user_id: number;
  username?: string;
  comment_count: number;
}

interface AdminErrorLog {
  id: number;
  user_id?: number;
  username?: string;
  action: string;
  payload?: string;
  error_message: string;
  created_at: string;
}

interface AdminPage {
  slug: string;
  title: string;
  content: string;
  updated_at?: string;
}

interface AdminShip {
  id: number;
  name: string;
  description?: string;
  is_active: number;
  created_at: string;
}

interface BannedWord {
  id: number;
  word: string;
  created_at: string;
}

interface InvitationBadge {
  id: number;
  required_invitations: number;
  badge_title: string;
  badge_emoji?: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface InvitationBadge {
  id: number;
  required_invitations: number;
  badge_title: string;
  badge_emoji?: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

type AdminTab = 'users' | 'legal' | 'logs' | 'comments' | 'reports' | 'errors' | 'ships' | 'blacklist' | 'badges';

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [comments, setComments] = useState<AdminComment[]>([]);
  const [reports, setReports] = useState<AdminCommentReport[]>([]);
  const [duckLogs, setDuckLogs] = useState<AdminDuckLog[]>([]);
  const [errorLogs, setErrorLogs] = useState<AdminErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imprint, setImprint] = useState<AdminPage | null>(null);
  const [privacy, setPrivacy] = useState<AdminPage | null>(null);
  const [savingPages, setSavingPages] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [ships, setShips] = useState<AdminShip[]>([]);
  const [shipNameInput, setShipNameInput] = useState('');
  const [shipDescriptionInput, setShipDescriptionInput] = useState('');
  const [shipSaving, setShipSaving] = useState(false);
  const [bannedWords, setBannedWords] = useState<BannedWord[]>([]);
  const [wordInput, setWordInput] = useState('');
  const [wordSaving, setWordSaving] = useState(false);
  const [badges, setBadges] = useState<InvitationBadge[]>([]);
  const [badgeForm, setBadgeForm] = useState({
    required_invitations: '',
    badge_title: '',
    badge_emoji: '',
    display_order: '',
  });
  const [badgeSaving, setBadgeSaving] = useState(false);
  const [editingBadgeId, setEditingBadgeId] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        usersResponse,
        commentsResponse,
        reportsResponse,
        duckLogsResponse,
        errorLogsResponse,
        imprintResponse,
        privacyResponse,
        shipsResponse,
        bannedWordsResponse,
        badgesResponse,
      ] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/comments'),
        api.get('/admin/comment-reports'),
        api.get('/admin/duck-logs'),
        api.get('/admin/error-logs'),
        api.get('/pages/imprint'),
        api.get('/pages/privacy'),
        api.get('/admin/ships'),
        api.get('/admin/banned-words'),
        api.get('/admin/invitation-badges'),
      ]);
      setUsers(usersResponse.data.users);
      setComments(commentsResponse.data.comments);
      setReports(reportsResponse.data.reports);
      setDuckLogs(duckLogsResponse.data.logs);
      setErrorLogs(errorLogsResponse.data.errorLogs);
      setImprint(imprintResponse.data.page);
      setPrivacy(privacyResponse.data.page);
      setShips((shipsResponse.data.ships ?? []).sort((a: AdminShip, b: AdminShip) => a.name.localeCompare(b.name)));
      setBannedWords(bannedWordsResponse.data.words ?? []);
      setBadges((badgesResponse.data.badges ?? []).sort((a: InvitationBadge, b: InvitationBadge) => a.required_invitations - b.required_invitations));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Admin-Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateStatus = async (userId: number, status: string) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { status });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Status konnte nicht aktualisiert werden');
    }
  };

  const toggleAdmin = async (userId: number, isAdmin: boolean) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { is_admin: isAdmin });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Admin-Rechte konnten nicht angepasst werden');
    }
  };

  const deleteUser = async (userId: number) => {
    if (!window.confirm('Benutzer wirklich löschen? Alle Funde und Kommentare werden entfernt.')) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Benutzer konnte nicht gelöscht werden');
    }
  };

  const deleteComment = async (commentId: number) => {
    try {
      await api.delete(`/admin/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setReports((prev) => prev.filter((report) => report.comment_id !== commentId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kommentar konnte nicht gelöscht werden');
    }
  };

  const resolveReport = async (reportId: number) => {
    try {
      await api.delete(`/admin/comment-reports/${reportId}`);
      setReports((prev) => prev.filter((report) => report.id !== reportId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Meldung konnte nicht entfernt werden');
    }
  };

  const deleteReportedComment = async (commentId: number, reportId: number) => {
    try {
      await api.delete(`/admin/comments/${commentId}`);
      await api.delete(`/admin/comment-reports/${reportId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setReports((prev) => prev.filter((report) => report.id !== reportId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kommentar konnte nicht entfernt werden');
    }
  };

  const deleteDuckLog = async (logId: number) => {
    if (!window.confirm('Diesen Fund wirklich löschen? Alle Kommentare gehen verloren.')) {
      return;
    }

    try {
      await api.delete(`/admin/duck-logs/${logId}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fund konnte nicht gelöscht werden');
    }
  };

  const deleteErrorLog = async (logId: number) => {
    try {
      await api.delete(`/admin/error-logs/${logId}`);
      setErrorLogs((prev) => prev.filter((log) => log.id !== logId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler-Log konnte nicht gelöscht werden');
    }
  };

  const createShip = async () => {
    if (!shipNameInput.trim()) {
      setError('Schiffsname darf nicht leer sein.');
      return;
    }
    try {
      setShipSaving(true);
      const response = await api.post('/admin/ships', {
        name: shipNameInput.trim(),
        description: shipDescriptionInput.trim() || undefined,
      });
      setShips((prev) =>
        [...prev, response.data.ship].sort((a, b) => a.name.localeCompare(b.name))
      );
      setShipNameInput('');
      setShipDescriptionInput('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Schiff konnte nicht angelegt werden');
    } finally {
      setShipSaving(false);
    }
  };

  const updateShip = async (ship: AdminShip, updates: Partial<AdminShip>) => {
    try {
      const response = await api.put(`/admin/ships/${ship.id}`, updates);
      setShips((prev) =>
        prev
          .map((s) => (s.id === ship.id ? response.data.ship : s))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Schiff konnte nicht aktualisiert werden');
    }
  };

  const toggleShipActive = (ship: AdminShip) => {
    updateShip(ship, { is_active: ship.is_active ? 0 : 1 });
  };

  const renameShip = (ship: AdminShip) => {
    const name = window.prompt('Neuer Schiffsname', ship.name);
    if (!name) {
      return;
    }
    const description = window.prompt(
      'Beschreibung (optional)',
      ship.description ?? ''
    );
    updateShip(ship, {
      name,
      description: description ?? '',
    });
  };

  const deleteShip = async (shipId: number) => {
    if (!window.confirm('Schiff wirklich löschen? Bereits geloggte Einträge behalten den Namen.')) {
      return;
    }
    try {
      await api.delete(`/admin/ships/${shipId}`);
      setShips((prev) => prev.filter((ship) => ship.id !== shipId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Schiff konnte nicht gelöscht werden');
    }
  };

  const savePages = async () => {
    if (!imprint || !privacy) return;
    try {
      setSavingPages(true);
      setError('');
      await Promise.all([
        api.put(`/pages/${imprint.slug}`, {
          title: imprint.title,
          content: imprint.content,
        }),
        api.put(`/pages/${privacy.slug}`, {
          title: privacy.title,
          content: privacy.content,
        }),
      ]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Rechtstexte konnten nicht gespeichert werden');
    } finally {
      setSavingPages(false);
    }
  };

  const addBannedWord = async () => {
    if (!wordInput.trim()) {
      setError('Bitte gib ein Wort ein');
      return;
    }
    try {
      setWordSaving(true);
      setError('');
      const response = await api.post('/admin/banned-words', { word: wordInput.trim() });
      setBannedWords((prev) => [...prev, response.data.word].sort((a, b) => a.word.localeCompare(b.word)));
      setWordInput('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Wort konnte nicht hinzugefügt werden');
    } finally {
      setWordSaving(false);
    }
  };

  const deleteBannedWord = async (wordId: number) => {
    if (!window.confirm('Wort wirklich von der Blacklist entfernen?')) {
      return;
    }
    try {
      await api.delete(`/admin/banned-words/${wordId}`);
      setBannedWords((prev) => prev.filter((word) => word.id !== wordId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Wort konnte nicht gelöscht werden');
    }
  };

  const handleBadgeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!badgeForm.required_invitations || !badgeForm.badge_title.trim()) {
      setError('Bitte fülle alle Pflichtfelder aus');
      return;
    }

    try {
      setBadgeSaving(true);
      setError('');
      if (editingBadgeId) {
        await api.put(`/admin/invitation-badges/${editingBadgeId}`, {
          required_invitations: parseInt(badgeForm.required_invitations, 10),
          badge_title: badgeForm.badge_title.trim(),
          badge_emoji: badgeForm.badge_emoji.trim() || null,
          display_order: badgeForm.display_order ? parseInt(badgeForm.display_order, 10) : 0,
        });
      } else {
        await api.post('/admin/invitation-badges', {
          required_invitations: parseInt(badgeForm.required_invitations, 10),
          badge_title: badgeForm.badge_title.trim(),
          badge_emoji: badgeForm.badge_emoji.trim() || null,
          display_order: badgeForm.display_order ? parseInt(badgeForm.display_order, 10) : 0,
        });
      }
      await fetchData();
      setBadgeForm({ required_invitations: '', badge_title: '', badge_emoji: '', display_order: '' });
      setEditingBadgeId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Badge konnte nicht gespeichert werden');
    } finally {
      setBadgeSaving(false);
    }
  };

  const handleEditBadge = (badge: InvitationBadge) => {
    setBadgeForm({
      required_invitations: String(badge.required_invitations),
      badge_title: badge.badge_title,
      badge_emoji: badge.badge_emoji || '',
      display_order: String(badge.display_order),
    });
    setEditingBadgeId(badge.id);
  };

  const handleCancelEdit = () => {
    setBadgeForm({ required_invitations: '', badge_title: '', badge_emoji: '', display_order: '' });
    setEditingBadgeId(null);
  };

  const handleDeleteBadge = async (badgeId: number) => {
    if (!window.confirm('Möchtest du diesen Badge wirklich löschen?')) {
      return;
    }
    try {
      await api.delete(`/admin/invitation-badges/${badgeId}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Badge konnte nicht gelöscht werden');
    }
  };

  if (loading) {
    return <div className="admin-panel"><p>Admin-Daten werden geladen...</p></div>;
  }

  return (
    <div className="admin-panel">
      <h1>Admin Panel</h1>
      {error && <div className="error-banner">{error}</div>}

      <div className="admin-tabs">
        <button
          type="button"
          className={activeTab === 'users' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('users')}
        >
          Mitglieder
        </button>
        <button
          type="button"
          className={activeTab === 'legal' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('legal')}
        >
          Rechtliches
        </button>
        <button
          type="button"
          className={activeTab === 'logs' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('logs')}
        >
          Duck-Funde
        </button>
        <button
          type="button"
          className={activeTab === 'comments' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('comments')}
        >
          Kommentare
        </button>
        <button
          type="button"
          className={activeTab === 'reports' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('reports')}
        >
          Meldungen
        </button>
        <button
          type="button"
          className={activeTab === 'errors' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('errors')}
        >
          Fehler-Logs
        </button>
        <button
          type="button"
          className={activeTab === 'ships' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('ships')}
        >
          Schiffe
        </button>
        <button
          type="button"
          className={activeTab === 'blacklist' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('blacklist')}
        >
          Blacklist
        </button>
        <button
          type="button"
          className={activeTab === 'badges' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('badges')}
        >
          🎁 Badges
        </button>
      </div>

      {activeTab === 'users' && (
      <section>
        <div className="section-header">
          <h2>Mitglieder</h2>
          <p>Aktive Benutzer verwalten, freischalten oder sperren.</p>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Benutzername</th>
                <th>E-Mail</th>
                <th>Status</th>
                <th>Admin</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>{u.username}</td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`status status-${u.status}`}>{u.status}</span>
                  </td>
                  <td>{u.is_admin ? 'Ja' : 'Nein'}</td>
                  <td className="actions">
                    <button onClick={() => updateStatus(u.id, 'active')}>Freischalten</button>
                    <button onClick={() => updateStatus(u.id, 'suspended')}>Sperren</button>
                    <button onClick={() => toggleAdmin(u.id, !u.is_admin)}>
                      {u.is_admin ? 'Admin entfernen' : 'Zum Admin machen'}
                    </button>
                    <button className="danger" onClick={() => deleteUser(u.id)}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'legal' && (
      <section>
        <div className="section-header">
          <h2>Rechtliches: Impressum & Datenschutz</h2>
          <p>Hier kannst du die Inhalte für Impressum und Datenschutz bearbeiten.</p>
        </div>
        <div className="legal-settings">
          {(!imprint || !privacy) && (
            <p className="muted">Seiten werden geladen oder sind nicht verfügbar.</p>
          )}
          {imprint && (
            <div className="legal-card">
              <h3>Impressum</h3>
              <label>
                Titel
                <input
                  type="text"
                  value={imprint.title}
                  onChange={(e) =>
                    setImprint({ ...imprint, title: e.target.value })
                  }
                />
              </label>
              <label>
                Inhalt
                <textarea
                  rows={8}
                  value={imprint.content}
                  onChange={(e) =>
                    setImprint({ ...imprint, content: e.target.value })
                  }
                />
              </label>
            </div>
          )}
          {privacy && (
            <div className="legal-card">
              <h3>Datenschutz</h3>
              <label>
                Titel
                <input
                  type="text"
                  value={privacy.title}
                  onChange={(e) =>
                    setPrivacy({ ...privacy, title: e.target.value })
                  }
                />
              </label>
              <label>
                Inhalt
                <textarea
                  rows={8}
                  value={privacy.content}
                  onChange={(e) =>
                    setPrivacy({ ...privacy, content: e.target.value })
                  }
                />
              </label>
            </div>
          )}
        </div>
        <div className="legal-actions">
          <button onClick={savePages} disabled={savingPages}>
            {savingPages ? 'Speichern…' : 'Rechtstexte speichern'}
          </button>
        </div>
      </section>
      )}

      {activeTab === 'logs' && (
      <section>
        <div className="section-header">
          <h2>Duck-Funde</h2>
          <p>Bis zu 200 jüngste Funde mit direkten Aktionen.</p>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Schiff</th>
                <th>Finder</th>
                <th>Gefunden am</th>
                <th>Kommentare</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {duckLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.id}</td>
                  <td>{log.ship_name}</td>
                  <td>{log.username ?? `User #${log.user_id}`}</td>
                  <td>{new Date(log.found_date).toLocaleDateString('de-DE')}</td>
                  <td>{log.comment_count}</td>
                  <td className="actions">
                    <Link to={`/log/${log.id}`} className="ghost-link" target="_blank" rel="noopener noreferrer">
                      Anzeigen
                    </Link>
                    <button className="danger" onClick={() => deleteDuckLog(log.id)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'comments' && (
      <section>
        <div className="section-header">
          <h2>Kommentare</h2>
          <p>Unangemessene Kommentare entfernen.</p>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Benutzer</th>
                <th>Inhalt</th>
                <th>Datum</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {comments.map((comment) => (
                <tr key={comment.id}>
                  <td>{comment.id}</td>
                  <td>{comment.username ?? `User #${comment.user_id}`}</td>
                  <td>{comment.content}</td>
                  <td>{new Date(comment.created_at).toLocaleString('de-DE')}</td>
                  <td>
                    <button className="danger" onClick={() => deleteComment(comment.id)}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'reports' && (
      <section>
        <div className="section-header">
          <h2>Gemeldete Kommentare</h2>
          <p>Prüfe Nutzer-Meldungen und reagiere darauf.</p>
        </div>
        {reports.length === 0 ? (
          <div className="empty-state">Aktuell liegen keine Meldungen vor.</div>
        ) : (
          <div className="reports-grid">
            {reports.map((report) => (
              <div key={report.id} className="report-card">
                <div className="report-meta">
                  <span className="chip">#{report.id}</span>
                  <span className="chip chip-muted">
                    {new Date(report.created_at).toLocaleString('de-DE')}
                  </span>
                </div>
                <p className="report-text">
                  <strong>Kommentar von {report.comment_user ?? 'Unbekannt'}:</strong>
                  <br />
                  {report.comment_content ?? 'Kommentar bereits entfernt'}
                </p>
                <p className="report-text">
                  <strong>Gemeldet von:</strong> {report.reporter_username ?? `User #${report.reporter_id}`}
                </p>
                {report.reason && (
                  <p className="report-reason">
                    <strong>Grund:</strong> {report.reason}
                  </p>
                )}
                <div className="report-actions">
                  <button onClick={() => resolveReport(report.id)}>Meldung erledigt</button>
                  {report.comment_id && (
                    <button
                      className="danger"
                      onClick={() => deleteReportedComment(report.comment_id, report.id)}
                    >
                      Kommentar löschen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {activeTab === 'errors' && (
      <section>
        <div className="section-header">
          <h2>Fehler-Logs</h2>
          <p>Zeigt die letzten Systemfehler (max. 200).</p>
        </div>
        {errorLogs.length === 0 ? (
          <div className="empty-state">Keine Fehler registriert.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Aktion</th>
                  <th>User</th>
                  <th>Nachricht</th>
                  <th>Datum</th>
                  <th>Payload</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {errorLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.id}</td>
                    <td>{log.action}</td>
                    <td>{log.username ?? (log.user_id ? `User #${log.user_id}` : '—')}</td>
                    <td>{log.error_message}</td>
                    <td>{new Date(log.created_at).toLocaleString('de-DE')}</td>
                    <td>
                      {log.payload ? (
                        <pre className="payload-preview">
                          {log.payload.length > 120
                            ? `${log.payload.slice(0, 120)}…`
                            : log.payload}
                        </pre>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <button className="danger" onClick={() => deleteErrorLog(log.id)}>
                        Entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {activeTab === 'ships' && (
        <section>
          <div className="section-header">
            <h2>Schiffsverzeichnis</h2>
            <p>Pflege die verfügbaren Schiffe für das Duck-Logging.</p>
          </div>
          <div className="ship-form">
            <input
              type="text"
              placeholder="Schiffsname"
              value={shipNameInput}
              onChange={(e) => setShipNameInput(e.target.value)}
            />
            <input
              type="text"
              placeholder="Beschreibung (optional)"
              value={shipDescriptionInput}
              onChange={(e) => setShipDescriptionInput(e.target.value)}
            />
            <button onClick={createShip} disabled={shipSaving}>
              {shipSaving ? 'Speichern…' : 'Schiff hinzufügen'}
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Beschreibung</th>
                  <th>Status</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {ships.map((ship) => (
                  <tr key={ship.id}>
                    <td>{ship.name}</td>
                    <td>{ship.description || '—'}</td>
                    <td>
                      <span className={`status ${ship.is_active ? 'status-active' : 'status-suspended'}`}>
                        {ship.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td>{new Date(ship.created_at).toLocaleDateString('de-DE')}</td>
                    <td className="actions">
                      <button onClick={() => toggleShipActive(ship)}>
                        {ship.is_active ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                      <button onClick={() => renameShip(ship)}>Bearbeiten</button>
                      <button className="danger" onClick={() => deleteShip(ship.id)}>
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
                {ships.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center' }}>
                      Noch keine Schiffe hinterlegt.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === 'blacklist' && (
        <section>
          <div className="section-header">
            <h2>Blacklist</h2>
            <p>Verwaltung der verbotenen Wörter für Kommentare und Dev-Chat.</p>
          </div>
          {error && <div className="admin-error">{error}</div>}
          <div className="admin-form">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <input
                type="text"
                value={wordInput}
                onChange={(e) => setWordInput(e.target.value)}
                placeholder="Wort eingeben..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addBannedWord();
                  }
                }}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.2)', background: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)' }}
              />
              <button
                type="button"
                onClick={addBannedWord}
                disabled={wordSaving || !wordInput.trim()}
                className="button-primary"
              >
                {wordSaving ? 'Hinzufügen...' : 'Hinzufügen'}
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Wort</th>
                  <th>Hinzugefügt am</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {bannedWords.map((word) => (
                  <tr key={word.id}>
                    <td><strong>{word.word}</strong></td>
                    <td>{new Date(word.created_at).toLocaleDateString('de-DE')}</td>
                    <td className="actions">
                      <button className="danger" onClick={() => deleteBannedWord(word.id)}>
                        Entfernen
                      </button>
                    </td>
                  </tr>
                ))}
                {bannedWords.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      Noch keine Wörter in der Blacklist.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === 'badges' && (
        <section>
          <div className="section-header">
            <h2>🎁 Einladungs-Badges</h2>
            <p>Verwalte die Badges, die Benutzer für erfolgreiche Einladungen erhalten.</p>
          </div>
          {error && <div className="admin-error">{error}</div>}
          <div className="admin-form">
            <form onSubmit={handleBadgeSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                <input
                  type="number"
                  value={badgeForm.required_invitations}
                  onChange={(e) => setBadgeForm((prev) => ({ ...prev, required_invitations: e.target.value }))}
                  placeholder="Anzahl Einladungen"
                  min="1"
                  required
                  style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.2)', background: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)' }}
                />
                <input
                  type="text"
                  value={badgeForm.badge_title}
                  onChange={(e) => setBadgeForm((prev) => ({ ...prev, badge_title: e.target.value }))}
                  placeholder="Badge-Titel (z.B. Einlader)"
                  required
                  style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.2)', background: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)' }}
                />
                <input
                  type="text"
                  value={badgeForm.badge_emoji}
                  onChange={(e) => setBadgeForm((prev) => ({ ...prev, badge_emoji: e.target.value }))}
                  placeholder="Emoji (z.B. 🎁)"
                  maxLength={2}
                  style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.2)', background: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)' }}
                />
                <input
                  type="number"
                  value={badgeForm.display_order}
                  onChange={(e) => setBadgeForm((prev) => ({ ...prev, display_order: e.target.value }))}
                  placeholder="Reihenfolge"
                  min="0"
                  style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.2)', background: 'rgba(255, 255, 255, 0.05)', color: 'var(--text-primary)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={badgeSaving}
                  className="button-primary"
                >
                  {badgeSaving ? 'Speichern...' : editingBadgeId ? 'Aktualisieren' : 'Hinzufügen'}
                </button>
                {editingBadgeId && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="ghost-button"
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </div>
          <table>
            <thead>
              <tr>
                <th>Anzahl Einladungen</th>
                <th>Badge</th>
                <th>Reihenfolge</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {badges.map((badge) => (
                <tr key={badge.id}>
                  <td><strong>{badge.required_invitations}</strong></td>
                  <td>
                    {badge.badge_emoji && <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>{badge.badge_emoji}</span>}
                    {badge.badge_title}
                  </td>
                  <td>{badge.display_order}</td>
                  <td className="actions">
                    <button onClick={() => handleEditBadge(badge)}>Bearbeiten</button>
                    <button className="danger" onClick={() => handleDeleteBadge(badge.id)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
              {badges.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center' }}>
                    Noch keine Badges konfiguriert.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
};

export default AdminPanel;
